/**
 * The client record setting's API client, for the owner card on Admin ›
 * Permissions:
 *
 *   GET /api/t/{slug}/v1/admin-review/client-record-access
 *       → { value, is_default, stored_value_recognised, last_changed }
 *   PUT /api/t/{slug}/v1/admin-review/client-record-access  { value }
 *       → { saved: true, changed, value, is_default: false }
 *
 * Owner-only server-side (manage_permissions); the card's visibility is
 * convenience, not security. House transport: raw fetch with ApiError
 * normalization (clientsApi.ts), so a failure carries its status and the
 * server's body.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import type { ClientRecordAccess } from './clientRecordAccess';

export interface ClientRecordAccessSetting {
  /** The setting the server applies: the stored value when recognised, else own_service. */
  value: ClientRecordAccess;
  /** True when nothing is stored, so the fallback applies. */
  is_default: boolean;
  /** False only when a stored value is present and is not one of the two settings. */
  stored_value_recognised: boolean;
  last_changed: { at: string; by_user_id: string; by_display_name: string | null } | null;
  request_id?: string;
}

export interface ClientRecordAccessSaveResult {
  saved: true;
  /** False when the stored value already equalled the request (another owner saved it first). */
  changed: boolean;
  value: ClientRecordAccess;
  is_default: false;
  request_id?: string;
}

/** The card's query key. */
export function clientRecordAccessKey(tenantSlug: string): readonly unknown[] {
  return ['admin-review', 'client-record-access', tenantSlug];
}

function settingUrl(slug: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1/admin-review/client-record-access`;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function fetchClientRecordAccess(
  tenantSlug: string,
  signal?: AbortSignal,
): Promise<ClientRecordAccessSetting> {
  const url = settingUrl(tenantSlug);
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<ClientRecordAccessSetting>(res, `GET ${url}`);
}

export async function saveClientRecordAccess(
  tenantSlug: string,
  value: ClientRecordAccess,
): Promise<ClientRecordAccessSaveResult> {
  const url = settingUrl(tenantSlug);
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  return parseOrThrow<ClientRecordAccessSaveResult>(res, `PUT ${url}`);
}
